// Player-facing wheel settings, kept in the param registry so they persist
// and show in the dev tuning panel like everything else.

import { param, setParam } from '../config/params';
import type { SimAids } from '../sim/car';

export const GEARBOX_MODES = ['Automatic', 'Paddles', 'H-shifter + clutch'] as const;
export type GearboxMode = 'auto' | 'sequential' | 'hpattern';

export const wheelParams = {
  rotation: param({ id: 'wheel.rotation', label: 'Wheel rotation (deg lock to lock)', group: 'Wheel', min: 180, max: 900, step: 10, value: 360 }),
  gain: param({ id: 'wheel.gain', label: 'FFB overall strength', group: 'Wheel', min: 0, max: 1.5, step: 0.05, value: 0.8 }),
  aligning: param({ id: 'wheel.aligning', label: 'FFB tyre forces (aligning torque)', group: 'Wheel', min: 0, max: 2, step: 0.05, value: 1 }),
  effects: param({ id: 'wheel.effects', label: 'FFB kerbs, bumps, impacts', group: 'Wheel', min: 0, max: 2, step: 0.05, value: 0.7 }),
  damper: param({ id: 'wheel.damper', label: 'FFB damper', group: 'Wheel', min: 0, max: 1, step: 0.05, value: 0.25 }),
  friction: param({ id: 'wheel.friction', label: 'FFB friction', group: 'Wheel', min: 0, max: 1, step: 0.05, value: 0.08 }),
  minForce: param({ id: 'wheel.minForce', label: 'FFB minimum force (gear deadband)', group: 'Wheel', min: 0, max: 0.2, step: 0.01, value: 0.04 }),
  /**
   * Torque at the front axle (N·m) that maps to full wheel force at gain 1. A Sepang lap
   * peaks ~420 N·m (T12-13), fast corners 300-400, hairpins ~200: at the default gain that's
   * ~70% in fast corners, ~35-40% in hairpins, leaving headroom for kerbs and impacts.
   */
  fullScaleNm: param({ id: 'wheel.fullScaleNm', label: 'FFB full scale (axle N·m)', group: 'Wheel', min: 100, max: 2000, step: 25, value: 450 }),
  smoothing: param({ id: 'wheel.smoothingHz', label: 'FFB smoothing cutoff (Hz)', group: 'Wheel', min: 5, max: 120, step: 1, value: 40 }),
  pedalDeadZone: param({ id: 'wheel.pedalDeadZone', label: 'Pedal dead zone', group: 'Wheel', min: 0, max: 0.15, step: 0.01, value: 0.03 }),
  /**
   * Brake output = pedal^curve. Without ABS the fronts lock at ~30% brake force at 80 km/h
   * and ~85% at 300 km/h; 1.4 spreads that over ~42-89% of the G27 pedal's travel, so the
   * slow end of a braking zone (bleeding off pressure) isn't squeezed into the first third.
   */
  brakeGamma: param({ id: 'wheel.brakeGamma', label: 'Brake pedal curve', group: 'Wheel', min: 0.5, max: 3, step: 0.1, value: 1.4 }),
  /** 0 automatic, 1 paddles (sequential), 2 H-shifter + clutch. */
  gearbox: param({ id: 'wheel.gearbox', label: 'Gearbox (0 auto, 1 paddles, 2 H)', group: 'Wheel', min: 0, max: 2, step: 1, value: 1 }),
  /** Traction control: 0 off (real F1), 1 low (slip-limited), 2 high. */
  tc: param({ id: 'wheel.tc', label: 'Traction control (0 off, 1 low, 2 high)', group: 'Wheel', min: 0, max: 2, step: 1, value: 1 }),
  /** Anti-lock brakes: 0 off (real F1), 1 on. */
  abs: param({ id: 'wheel.abs', label: 'Anti-lock brakes', group: 'Wheel', min: 0, max: 1, step: 1, value: 0 }),
  /** Stability aid: 0 off (real F1), 1 on (mild yaw damping once the rear slides). */
  stability: param({ id: 'wheel.stability', label: 'Stability aid', group: 'Wheel', min: 0, max: 1, step: 1, value: 0 }),
  leds: param({ id: 'wheel.leds', label: 'Rev LEDs', group: 'Wheel', min: 0, max: 1, step: 1, value: 1 }),
};

export function gearboxMode(): GearboxMode {
  const v = Math.round(wheelParams.gearbox.value);
  return v === 0 ? 'auto' : v === 2 ? 'hpattern' : 'sequential';
}

/** The aids chosen in the wheel settings. */
export function simAids(): SimAids {
  const tc = Math.round(wheelParams.tc.value);
  return { tc: tc <= 0 ? 0 : tc >= 2 ? 2 : 1, abs: wheelParams.abs.value > 0, stability: wheelParams.stability.value > 0 };
}

/** One-tap aid presets for the settings screen; anything else shows as Custom. */
export const AID_PRESETS: ReadonlyArray<{ name: string; aids: SimAids }> = [
  { name: 'Realistic (all off)', aids: { tc: 0, abs: false, stability: false } },
  { name: 'Approachable (TC low)', aids: { tc: 1, abs: false, stability: false } },
  { name: 'Beginner (all on)', aids: { tc: 2, abs: true, stability: true } },
];

/** Index of the preset matching the current aids, or −1 for a custom mix. */
export function aidPreset(): number {
  const a = simAids();
  return AID_PRESETS.findIndex((p) => p.aids.tc === a.tc && p.aids.abs === a.abs && p.aids.stability === a.stability);
}

export function applyAidPreset(index: number): void {
  const p = AID_PRESETS[((index % AID_PRESETS.length) + AID_PRESETS.length) % AID_PRESETS.length]!.aids;
  setParam(wheelParams.tc.id, p.tc);
  setParam(wheelParams.abs.id, p.abs ? 1 : 0);
  setParam(wheelParams.stability.id, p.stability ? 1 : 0);
}
